import { motion } from "framer-motion";
import {
  Zap, Droplets, Hammer, Home, Wind, TreeDeciduous, Paintbrush, Waves,
  Sparkles, Scissors, Bug, Dumbbell, Stethoscope, Truck, Heart, Wrench,
  type LucideIcon,
} from "lucide-react";

type Industry = { icon: LucideIcon; title: string };

const trades: Industry[] = [
  { icon: Zap, title: "Electricians" },
  { icon: Droplets, title: "Plumbers" },
  { icon: Hammer, title: "Builders" },
  { icon: Home, title: "Roofers" },
  { icon: Wind, title: "HVAC" },
  { icon: TreeDeciduous, title: "Landscapers" },
  { icon: Paintbrush, title: "Painters" },
  { icon: Waves, title: "Pool Builders" },
];

const services: Industry[] = [
  { icon: Sparkles, title: "Cleaning" },
  { icon: Scissors, title: "Hair & Beauty" },
  { icon: Bug, title: "Pest Control" },
  { icon: Dumbbell, title: "Gyms & Fitness" },
  { icon: Stethoscope, title: "Vet Clinics" },
  { icon: Heart, title: "Beauty Services" },
  { icon: Truck, title: "Removalists" },
  { icon: Wrench, title: "Home Services" },
];

function Chip({ industry }: { industry: Industry }) {
  const Icon = industry.icon;
  return (
    <div className="inline-flex min-h-14 items-center gap-3 px-6 py-3 mx-2 rounded-full bg-card border border-border/60 shadow-card">
      <Icon className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={2.25} />
      <span className="text-base sm:text-lg font-semibold text-foreground/90 whitespace-nowrap">
        {industry.title}
      </span>
    </div>
  );
}

function Marquee({
  items,
  reverse = false,
  duration = 40,
}: {
  items: Industry[];
  reverse?: boolean;
  duration?: number;
}) {
  // Duplicate enough times to ensure seamless loop on wide screens
  const loop = [...items, ...items, ...items];
  return (
    <div
      className="relative overflow-hidden"
      style={{
        maskImage:
          "linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%)",
      }}
    >
      <motion.div
        className="flex w-max"
        animate={{ x: reverse ? ["-33.333%", "0%"] : ["0%", "-33.333%"] }}
        transition={{ duration, ease: "linear", repeat: Infinity }}
      >
        {loop.map((industry, i) => (
          <Chip key={`${industry.title}-${i}`} industry={industry} />
        ))}
      </motion.div>
    </div>
  );
}

export function IndustriesSection() {
  return (
    <section
      className="section-padding bg-background relative overflow-hidden"
      aria-labelledby="industries-heading"
    >
      <div className="absolute inset-0 grid-pattern opacity-20" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] bg-primary/[0.04] rounded-full blur-[140px]" />

      <div className="container-wide relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center mb-10 sm:mb-12"
        >
          <div className="section-badge mb-3 sm:mb-4 text-xs">
            <span>Who We Work With</span>
          </div>
          <h2
            id="industries-heading"
            className="heading-section text-foreground mb-3 text-balance"
          >
            Built for <span className="text-gradient">trade & service businesses</span> that win locally
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-[1.65]">
            If you serve customers in a defined area and earn from jobs, bookings or appointments — our system fits your business.
          </p>
        </motion.div>

        <div className="space-y-5 sm:space-y-6">
          <Marquee items={trades} duration={45} />
          <Marquee items={services} duration={50} reverse />
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-center text-muted-foreground text-base sm:text-lg mt-10 max-w-2xl mx-auto leading-relaxed"
        >
          Don’t see your industry here? If you serve a local area, we can likely build a system around it — contact us to check fit.
        </motion.p>
      </div>
    </section>
  );
}
